import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Card,
  Button,
  Stack,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  useLocation
} from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { makeStyles } from "@mui/styles";
import SelectDropdown from "../common/FormSelectDropdown";
import TextInput from "../common/TextInput";
import { useLoader } from "../../../pages/LoaderContext";
import ValidationModal from "../common/ValidationModal"
import { useProjectApiClient } from "../../Services/api/projectService"
import { usePlanningStrategyApiClient } from "../../Services/api/planningStrategyService"
import { successToast, errorToast } from "../../utils/toast";
import { ToastContainer } from 'react-toastify';
import { useNavigate, useParams } from "react-router-dom";
import UnSavedModal from "../common/UnSavedModal"
import { useAppContext } from '../../../hooks/context/AppContext';
import AutoSaveModal from "../common/AutoSaveModal"
import useIdle from '../../../hooks/useIdle';
import { projectVersion } from "config/navConfig";
import FileUpload from "../common/FileUpload";
import UploadList from "../common/UploadsList";
import circleArrowIcon from "../../../assets/images/circle-arrow-icon.svg"
import uploadIcon from "../../../assets/images/upload-icon.svg";


const useStyles = makeStyles({
  themeButton: {
    backgroundColor: "#2a3fbf !important",
    padding: "8px 30px",
    fontWeight: "700",
    "&:hover": { backgroundColor: "#2a3fbf !important" },
  },
  cancelButton: {
    backgroundColor: "#d9d9d9 !important",
    color: "#626262 !important",
    padding: "8px 30px",
    fontWeight: "700",
    "&:hover": {
      backgroundColor: "#d9d9d9 !important",
      color: "#626262 !important",
    },
  },
});
interface Option {
  key: string | number;
  value: string;
}
interface AuditEntry {
  fieldName: string;
  oldValue: string;
  newValue: string;
}

const PlanningStrategyForm: React.FC = () => {
  const location = useLocation();
  const {
    getProjectDetails,
    getRequiredField,
    publishProjectOnSave,
    uploadProjectFile
  } = useProjectApiClient();
  const { projectId, projectKey } = useParams<{ projectKey: string; projectId: string }>();
  const {
    createPlanningStrategy,
    getcm,
    getCMRegion,
    getCptCompliance,
    getInHouse,
    getPlanningStrategy,
    getPlanningStrategyRisk,
    getSlaComplete,
    getSourceUnitCountry,
    getSourcingUnitRegion,
    getSupplierUtilisation,
    getUnits,
  } = usePlanningStrategyApiClient();
  const { setLoading } = useLoader();
  const { appData, updateAppData, userData } = useAppContext();
  const classes = useStyles();
  const navigate = useNavigate();
  const [volumeY1Unit, setVolumeY1Unit] = useState<Option[]>([]);
  const [volumeY3Unit, setVolumeY3Unit] = useState<Option[]>([]);
  const [inHouse, setInHouse] = useState([]);
  const [sourcingUnitRegion, setSourcingUnitRegion] = useState([]);
  const [sourcingUnitCountry, setSourcingUnitCountry] = useState([]);
  const [cmRegion, setCmRegion] = useState([]);
  const [sourcingCountryOptions, setSourcingCountryOptions] = useState<Record<any, any[]>>({});
  // const [cms, setCms] = useState([]);
  const [cmsOptions, setCmsOptions] = useState<Record<any, any[]>>({});
  const [slaComplete, setSlaComplete] = useState([]);
  const [supplierUtilisationStrategy, setSupplierUtilisationStrategy] = useState([]);
  const [cptCompliance, setcptCompliance] = useState([]);
  const [risks, setRisks] = useState([]);
  const [isExisting, setIsExisting] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [inHouseOptions, setInHouseOptions] = useState<{ key: number; value: string }[]>([]);
  const [selectedInHouse, setSelectedInHouse] = useState<number | null>(null);
  const [validationErrors, setValidationErrors] = useState({})
  const [isValidationError, setIsValidationError] = useState(false)
  const [projectDetails, setProjectDetails] = useState({
    issaved: false
  })
  const [isDraftDisabled, setIsDraftDisabled] = useState(false);
  const [autoSaveModal, setAutoSaveModal] = React.useState<boolean>(false);
  const isIdle = useIdle(60000 * 10)
  const hasAutoSaved = useRef(false);
  const [isUpload, setIsUpload] = React.useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  interface InhouseCMRow {
    PSIC_ID?: number | null;
    inHouse: string;
    sourcingUnitRegion: string;
    sourcingUnitCountry: string;
    cmRegion: string;
    cms: string;
  }

  const schema = yup.object().shape({
    volumeY1: yup.number()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .typeError("Volume Y1 must be a number")
      .required("Volume Y1 is required"),
    volumeY1Unit: yup.string().required("Volume Y1 unit is required"),
    volumeY3: yup.number()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .typeError("volume Y3 must be a number")
      .required("volume Y3 is required"),
    volumeY3Unit: yup.string().required("Volume Y3 unit is required"),
    cannibalisation: yup
      .mixed()
      .nullable()
      .transform((value, originalValue) => {
        if (originalValue === "" || originalValue === null || originalValue === undefined) {
          return null;
        }
        return typeof value === "number" && !isNaN(value) ? value : Number(value);
      })
      .test("is-valid-number", "Available Capacity must be a number", (value) => {
        return value === null || typeof value === "number";
      }),
    inHouse: yup.mixed().nullable(),
    sourcingUnitRegion: yup.mixed().nullable(),
    sourcingUnitCountry: yup.mixed().nullable(),
    cmRegion: yup.mixed().nullable(),
    cms: yup.mixed().nullable(),
    slaComplete: yup.mixed().nullable(),
    availableCapacity: yup
      .mixed()
      .nullable()
      .transform((value, originalValue) => {
        if (originalValue === "" || originalValue === null || originalValue === undefined) {
          return null;
        }
        return typeof value === "number" && !isNaN(value) ? value : Number(value);
      })
      .test("is-valid-number", "Available Capacity must be a number", (value) => {
        return value === null || typeof value === "number";
      }),

    supplierUtilisationStrategy: yup.mixed().nullable(),
    cptCompliance: yup.mixed().nullable(),
    comment: yup
      .string()
      .nullable()
      .transform((value, originalValue) => {
        if (originalValue === null || originalValue === undefined) {
          return "";
        }
        return value;
      })
      .test("not-null", "Comment cannot be null", (value) => {
        return value !== null;
      }),
    risk: yup.mixed().nullable(),
  });
  interface InhouseCmRow {
    inHouse: string;
    sourcingUnitRegion: string;
    sourcingUnitCountry: string;
    cmRegion: string;
    cms: string;
  }

  interface PlanningStrategyFormData {
    volumeY1: string;
    volumeY1Unit: string;
    volumeY3: string;
    volumeY3Unit: string;
    cannibalisation: string;
    supplierUtilisationStrategy: string;
    slaComplete: string;
    availableCapacity: string;
    cptCompliance: string;
    risk: string;
    comment: string;
    inhouseCmList: InhouseCMRow[];
  }


  const {
    handleSubmit,
    control,
    clearErrors,
    reset,
    setValue,
    formState: { errors },
    watch,
    setError,
  } = useForm<PlanningStrategyFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      inhouseCmList: [
        { inHouse: "", sourcingUnitRegion: "", sourcingUnitCountry: "", cmRegion: "", cms: "" },
      ],
    },
  });

  const [formData, setFormData] = useState<PlanningStrategyFormData>({
    volumeY1: "",
    volumeY1Unit: "",
    volumeY3: "",
    volumeY3Unit: "",
    cannibalisation: "",
    supplierUtilisationStrategy: "",
    slaComplete: "",
    availableCapacity: "",
    cptCompliance: "",
    risk: "",
    comment: "",
    inhouseCmList: [
      {
        PSIC_ID: null,
        inHouse: "",
        sourcingUnitRegion: "",
        sourcingUnitCountry: "",
        cmRegion: "",
        cms: "",
      },
    ],
  });

  const [initialformData, setInitialformData] = useState<PlanningStrategyFormData>({
    volumeY1: "",
    volumeY1Unit: "",
    volumeY3: "",
    volumeY3Unit: "",
    cannibalisation: "",
    supplierUtilisationStrategy: "",
    slaComplete: "",
    availableCapacity: "",
    cptCompliance: "",
    risk: "",
    comment: "",
    inhouseCmList: [
      {
        PSIC_ID: null,
        inHouse: "",
        sourcingUnitRegion: "",
        sourcingUnitCountry: "",
        cmRegion: "",
        cms: "",
      },
    ],
  });

  useEffect(() => {
    const subscription = watch((values) => {
      if (values?.inhouseCmList) {
        setFormData((prev: any) => ({
          ...prev,
          // @ts-ignore
          inhouseCmList: values.inhouseCmList.map((item: any, index: number) => ({
            ...prev.inhouseCmList[index],
            ...item,
          })),
        }));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // useFieldArray to manage dynamic inhouse/Cm rows used below
  const { fields, append, remove } = useFieldArray({
    control,
    name: "inhouseCmList",
  });


  useEffect(() => {
    if (userData) {
      setIsDisabled(!userData.permissions.CanEdit)
    }
  }, [userData]);


  useEffect(() => {
    if (isIdle && !autoSaveModal) {
      let check = JSON.stringify(formData) === JSON.stringify(initialformData);
      if (!check && !hasAutoSaved.current) {
        setAutoSaveModal(true);
      }
    }
  }, [isIdle]);

  // 👇 Place the new hydration effect here
  useEffect(() => {
    fields.forEach((field, idx) => {
      const row = watch(`inhouseCmList.${idx}`);
      if (!row) return;

      if (row.inHouse === "39" && row.sourcingUnitRegion) {
        fetchSourceUnitCountry(Number(row.sourcingUnitRegion), field.id);
      }
      if (row.inHouse === "40" && row.cmRegion) {
        fetchCM(Number(row.cmRegion), field.id);
      }
    });
  }, [fields, watch]);

  useEffect(() => {
    setLoading(true)
    const fetchOptions = async () => {
      await Promise.all([
        fetchPlanningStrategyRisk(),
        fetchInHouse(),
        fetchSupplierUtilisation(),
        fetchCptCompliance(),
        fetchVolumeY1Unit(),
        fetchvolumeY3Unit(),
        // fetchCannibalisation(),
        fetchSlaComplete(),
        fetchCMRegion(),
        fetchCM(),
        fetchSourcingUnitRegion(),
        fetchSourceUnitCountry(),
        fetchsProjectDetails()
      ]);
    };
    fetchOptions();
    if (projectKey) {
      setTimeout(() => {
        fetchPlanningStrategy(projectKey);
      }, 1000);
    }
  }, [projectKey]);
  // useEffect(() => { });

  useEffect(() => {
    let check = JSON.stringify(formData) == JSON.stringify(initialformData);
    if (!check) {
      updateAppData({ isFormChanged: true, showUnSavedModal: false });
    } else {
      updateAppData({ isFormChanged: false, showUnSavedModal: false });
    }

  }, [formData]);


  const fetchsProjectDetails = async () => {
    try {
      const data = await getProjectDetails(projectId);
      if (data) {
        setProjectDetails({ issaved: data.issaved })
        setIsDraftDisabled(data.issaved)
      }

    } catch (error) {
      console.error(" Failed to fetch projects:", error);
    } finally {
    }
  };

  const isEmptyObject = (obj: object) => {
    return JSON.stringify(obj) === '{}'
  };


  const fetchPlanningStrategy = async (key: string) => {
    try {
      const response: any = await getPlanningStrategy(key);
      setLoading(false)
      if (!isEmptyObject(response.data)) {
        setIsExisting(true)
        const data = response.data;
        const planningStrategyData: PlanningStrategyFormData = {
          volumeY1: String(data.Volumey1 ?? ""),
          volumeY1Unit: data.Volumey1_Measure ?? "",
          volumeY3: String(data.Volumey3 ?? ""),
          volumeY3Unit: data.Volumey3_Measure ?? "",
          cannibalisation: data.Cannibalisation ? String(data.Cannibalisation) : "",
          supplierUtilisationStrategy: data.Supplier_Utilisation_Strategy_key ? String(data.Supplier_Utilisation_Strategy_key) : "",
          slaComplete: data.SLA_Complete_Key ? String(data.SLA_Complete_Key) : "",
          availableCapacity: data.Avaialble_Capacity ? String(data.Avaialble_Capacity) : "",
          cptCompliance: data.Cpt_Compliance_Key ? String(data.Cpt_Compliance_Key) : "",
          risk: data.Risk_Key ? String(data.Risk_Key) : "",
          comment: data.Comment ?? "",
          inhouseCmList:
            data.Inhouse_CM_List && data.Inhouse_CM_List.length > 0
              ? data.Inhouse_CM_List.map((row: any) => ({
                PSIC_ID: row.PSIC_ID ?? null,
                inHouse: row.InHouse_CM_Key ? String(row.InHouse_CM_Key) : "",
                sourcingUnitRegion: row.Sourcing_Unit_Region ? String(row.Sourcing_Unit_Region) : "",
                sourcingUnitCountry: row.Sourcing_Unit_country ? String(row.Sourcing_Unit_country) : "",
                cmRegion: row.CM_region ? String(row.CM_region) : "",
                cms: row.Cm ? String(row.Cm) : "",
              }))
              : [
                {
                  PSIC_ID: null,
                  inHouse: "",
                  sourcingUnitRegion: "",
                  sourcingUnitCountry: "",
                  cmRegion: "",
                  cms: "",
                },
              ],

        };
        setFormData(planningStrategyData);
        setInitialformData(planningStrategyData);

        // Ensure form values are updated with a slight delay
        // window.setTimeout(() => {
        //   Object.keys(planningStrategyData).forEach((key) => {
        //     setValue(
        //       key as any,
        //       planningStrategyData[key as keyof typeof planningStrategyData]
        //     );
        //   });
        // }, 0);


        // Reset full form (including field array values)
        reset(planningStrategyData);

      }
    }
    catch (error) {
      console.error("Failed to fetch planning strategy:", error);
    }
  };

  const fetchPlanningStrategyRisk = async () => {
    try {
      const data = await getPlanningStrategyRisk();
      let output = data.map((item: any) => ({
        key: item.Risk_key,
        value: item.Risk_name,
      }));
      setRisks(output);
    } catch (error) {
      console.error(" Failed to fetch projects:", error);
    } finally {
    }
  };


  const fetchInHouse = async () => {
    try {
      const response = await getInHouse();
      const formattedData = response.map((item: any) => ({
        key: item.Lookup_Key,
        value: item.Valuez,
      }));
      setInHouseOptions(formattedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };




  const fetchSupplierUtilisation = async () => {
    try {
      const data = await getSupplierUtilisation();
      let output = data.map((item: any) => ({
        key: item.Lookup_Key,
        value: item.Valuez,
      }));
      setSupplierUtilisationStrategy(output);
    } catch (error) {
      console.error(" Failed to fetch Supplier Utilisation:", error);
    } finally {
    }
  };
  const fetchCptCompliance = async () => {
    try {
      const data = await getCptCompliance();
      let output = data.map((item: any) => ({
        key: item.Status_Key,
        value: item.Status_Name,
      }));
      // @ts-ignore
      setcptCompliance(output);
    } catch (error) {
      console.error(" Failed to fetch CPT Compliance:", error);
    } finally {
    }
  };
  const fetchvolumeY3Unit = async () => {
    try {
      const data = await getUnits();
      let output = data.map((item: any) => ({
        key: item.key,
        value: item.value,
      }));
      // @ts-ignore
      setVolumeY3Unit(output);
    } catch (error) {
      console.error(" Failed to fetch VolumeY3 Unit:", error);
    } finally {
    }
  };

  const fetchVolumeY1Unit = async () => {
    try {
      const data = await getUnits();
      let output = data.map((item: any) => ({
        key: item.key,
        value: item.value,
      }));
      // @ts-ignore
      setVolumeY1Unit(output);
    } catch (error) {
      console.error(" Failed to fetch VolumeY1 Unit:", error);
    } finally {
    }
  };

  // const fetchCannibalisation = async () => {
  //   try {
  //     const data = await getCannibalisation();
  //     let output = data.map((item: any) => ({
  //       key: item.Lookup_Key,
  //       value: item.Valuez,
  //     }));
  //     // @ts-ignore
  //     setCannibalisation(output);
  //   } catch (error) {
  //     console.error(" Failed to fetch Cannibalisation:", error);
  //   } finally {
  //   }
  // };
  const fetchSlaComplete = async () => {
    try {
      const data = await getSlaComplete();
      let output = data.map((item: any) => ({
        key: item.Lookup_Key,
        value: item.Valuez,
      }));
      // @ts-ignore
      setSlaComplete(output);
    } catch (error) {
      console.error(" Failed to fetch Sla Complete:", error);
    } finally {
    }
  };
  const fetchCMRegion = async () => {
    try {
      const data = await getCMRegion();
      let output = data.map((item: any) => ({
        key: item.CMRegion_Key,
        value: item.CMRegion_Name,
      }));
      // @ts-ignore
      setCmRegion(output);
    } catch (error) {
      console.error(" Failed to fetch CM Region:", error);
    } finally {
    }
  };

  const fetchSourcingUnitRegion = async () => {
    try {
      const data = await getSourcingUnitRegion();
      let output = data.map((item: any) => ({
        key: item.SourceUnitRegion_Key,
        value: item.SourceUnitRegion_Name,
      }));
      // @ts-ignore
      setSourcingUnitRegion(output);
    } catch (error) {
      console.error(" Failed to fetch getsource Unit Region:", error);
    } finally {
    }
  };
  const fetchCM = async (regionKey?: number, rowKey?: string | number) => {
    if (!rowKey) return;
    try {
      const data = await getcm(regionKey);
      const output = data.map((item: any) => ({
        key: item.CM_Key,
        value: item.CM_Name,
      }));
      setCmsOptions((prev) => ({ ...prev, [rowKey]: output }));
    } catch (error) {
      console.error("Failed to fetch CM:", error);
    }
  };

  const fetchSourceUnitCountry = async (regionKey?: number, rowKey?: string | number) => {
    if (!rowKey) return;
    try {
      const data = await getSourceUnitCountry(regionKey);
      const output = data.map((item: any) => ({
        key: item.SourceUnit_key,
        value: item.Country_Name,
      }));
      setSourcingCountryOptions((prev) => ({ ...prev, [rowKey]: output }));
    } catch (error) {
      console.error("Failed to fetch Sourcing Unit Country:", error);
    }
  };



  const handleFormSubmit = (type: any, url: string) => {
    return handleSubmit((data) => onSubmit(data, type, url));
  };

  const toNullIfEmpty = (value: any) =>
    value === "null" || value === "undefined" || value === undefined || value === "" ? null : value;


  const fetchRequiredField = async () => {
    try {
      const data = await getRequiredField(projectKey);
      return data
    } catch (error) {
      console.error(" Failed to fetch projects:", error);
    } finally {

    }
  };

  let groupByModule = (errors: any) => {
    return errors.reduce((grouped: any, error: any) => {
      const { ModuleName, Message } = error;
      if (!grouped[ModuleName]) {
        grouped[ModuleName] = [];
      }
      grouped[ModuleName].push(Message);
      return grouped;
    }, {});
  }

  let closeValidationModal = () => {
    setIsValidationError(false)
    setValidationErrors({})
  }
  let pubLishProjectData = async () => {
    let res = await publishProjectOnSave(projectKey)
  }
  function serializeForAudit(value: any) {
    if (Array.isArray(value)) {
      return value.map(v => JSON.stringify(v)).join(" | ");
    }
    if (typeof value === "object" && value !== null) {
      return JSON.stringify(value);
    }
    return value;
  }

  const getCompleteAuditTrail = (initialData: Record<string, any>, updatedData: Record<string, any>): AuditEntry[] => {
    const audit: AuditEntry[] = [];
    const keys = Object.keys(initialData);
    for (const key of keys) {
      if (key === 'project_id') continue;

      const oldValue = serializeForAudit(initialData[key]);
      const newValue = serializeForAudit(updatedData[key]);

      audit.push({
        fieldName: key,
        oldValue: String(oldValue),
        newValue: String(newValue)
      });
    }

    return audit;
  }

  // const onSubmit = async (data: any, type: any, url: string) => {
  //   let resField = await fetchRequiredField()
  //   if (resField?.data && resField?.data.filter((val: any) => val.ModuleName !== 'Planning Strategy').length) {
  //     const groupedErrors = groupByModule(resField?.data.filter((val: any) => val.ModuleName !== 'Planning Strategy'));
  //     setValidationErrors(groupedErrors)
  //     setIsValidationError(true)
  //     return
  //   }
  //   try {
  //     setLoading(true)
  //     let payload = {
  //       Project_Key: projectKey,
  //       Volumey1: data.volumeY1,
  //       Volumey1_Measure: data.volumeY1Unit,
  //       Volumey3: data.volumeY3,
  //       Volumey3_Measure: data.volumeY3Unit,
  //       Cannibalisation: toNullIfEmpty(data.cannibalisation),
  //       InHouse_CM_Key: toNullIfEmpty(data.inHouse),
  //       Sourcing_Unit_Region: toNullIfEmpty(data.sourcingUnitRegion),
  //       Sourcing_Unit_country: toNullIfEmpty(data.sourcingUnitCountry),
  //       CM_region: toNullIfEmpty(data.cmRegion),
  //       Cm: toNullIfEmpty(data.cms),
  //       SLA_Complete_Key: toNullIfEmpty(data.slaComplete),
  //       Avaialble_Capacity: toNullIfEmpty(data.availableCapacity),
  //       Supplier_Utilisation_Strategy_key: toNullIfEmpty(
  //         data.supplierUtilisationStrategy
  //       ),
  //       Cpt_Compliance_Key: toNullIfEmpty(data.cptCompliance),
  //       Comment: toNullIfEmpty(data.comment),
  //       Risk_Key: toNullIfEmpty(data.risk),
  //     };

  //     let initialPayload = {
  //       Project_Key: projectKey,
  //       Volumey1: initialformData.volumeY1,
  //       Volumey1_Measure: initialformData.volumeY1Unit,
  //       Volumey3: initialformData.volumeY3,
  //       Volumey3_Measure: initialformData.volumeY3Unit,
  //       Cannibalisation: toNullIfEmpty(initialformData.cannibalisation),
  //       InHouse_CM_Key: toNullIfEmpty(initialformData.inHouse),
  //       Sourcing_Unit_Region: toNullIfEmpty(initialformData.sourcingUnitRegion),
  //       Sourcing_Unit_country: toNullIfEmpty(initialformData.sourcingUnitCountry),
  //       CM_region: toNullIfEmpty(initialformData.cmRegion),
  //       Cm: toNullIfEmpty(initialformData.cms),
  //       SLA_Complete_Key: toNullIfEmpty(initialformData.slaComplete),
  //       Avaialble_Capacity: toNullIfEmpty(initialformData.availableCapacity),
  //       Supplier_Utilisation_Strategy_key: toNullIfEmpty(
  //         initialformData.supplierUtilisationStrategy
  //       ),
  //       Cpt_Compliance_Key: toNullIfEmpty(initialformData.cptCompliance),
  //       Comment: toNullIfEmpty(initialformData.comment),
  //       Risk_Key: toNullIfEmpty(initialformData.risk),
  //     };
  //     if (data.inHouse === "39") {
  //       payload.Cm = null;
  //       payload.CM_region = null;
  //     } else if (data.inHouse === "40") {
  //       payload.Sourcing_Unit_Region = null;
  //       payload.Sourcing_Unit_country = null;
  //     }
  //     const completeAuditTrail = getCompleteAuditTrail(initialPayload, payload);
  //     {/* @ts-ignore */ }
  //     payload.audit = completeAuditTrail;
  //     if (!isExisting) {
  //       {/* @ts-ignore */ }
  //       let res = await createPlanningStrategy(payload);
  //       await pubLishProjectData();
  //       setProjectDetails({ issaved: true });
  //       setIsDraftDisabled(true)
  //       updateAppData({ isFormChanged: false, showUnSavedModal: false, url: appData.url });
  //       setLoading(false)
  //       successToast("Planning Strategy saved successfully!");
  //       reset();
  //       setTimeout(() => {
  //         navigate(url ? url : `${projectVersion}/project/planning-strategy/${projectId}/${projectKey}`);
  //       }, 3000);
  //     } else {
  //       {/* @ts-ignore */ }
  //       let res = await createPlanningStrategy(payload, projectKey)
  //       if (projectKey) {
  //         fetchPlanningStrategy(projectKey);
  //       }
  //       await pubLishProjectData();
  //       setProjectDetails({ issaved: true });
  //       setIsDraftDisabled(true)
  //       updateAppData({ isFormChanged: false, showUnSavedModal: false, url: appData.url });
  //       setLoading(false)
  //       if (res) {
  //         {/* @ts-ignore */ }
  //         successToast(res.message)
  //         setTimeout(() => {
  //           navigate(`${projectVersion}/project/planning-strategy/${projectId}/${projectKey}`);
  //         }, 2000);
  //       }
  //     }
  //   } catch (error) {
  //     console.error(" Error submitting form:", error);
  //     setLoading(false)

  //   }
  // };

  const buildInhouseCmPayload = (
    currentRows: any[],
    initialRows: any[]
  ) => {
    const payload: any[] = [];

    // Normalize row for comparison
    const normalize = (row: any) => ({
      inHouse: row.inHouse || "",
      sourcingUnitRegion: row.sourcingUnitRegion || "",
      sourcingUnitCountry: row.sourcingUnitCountry || "",
      cmRegion: row.cmRegion || "",
      cms: row.cms || "",
    });

    // Create a map for initial rows by ID (if exists)
    const initialMap = new Map(
      initialRows.map((row: any) => [row.PSIC_ID || JSON.stringify(normalize(row)), row])
    );

    // Handle current rows → detect added/updated/unchanged
    currentRows.forEach((row) => {
      const key = row.PSIC_ID || JSON.stringify(normalize(row));
      const initialRow = initialMap.get(key);

      let status: string | null = null;

      if (!initialRow) {
        status = "added";
      } else if (JSON.stringify(normalize(initialRow)) !== JSON.stringify(normalize(row))) {
        status = "updated";
        initialMap.delete(key); // processed
      } else {
        status = null; // no change
        initialMap.delete(key); // processed
      }

      payload.push({
        PSIC_ID: row.PSIC_ID || null,
        InHouse_CM_Key: toNullIfEmpty(row.inHouse),
        Sourcing_Unit_Region: row.inHouse === "39" ? toNullIfEmpty(row.sourcingUnitRegion) : null,
        Sourcing_Unit_country: row.inHouse === "39" ? toNullIfEmpty(row.sourcingUnitCountry) : null,
        CM_region: row.inHouse === "40" ? toNullIfEmpty(row.cmRegion) : null,
        Cm: row.inHouse === "40" ? toNullIfEmpty(row.cms) : null,
        Status: status,
      });
    });

    // Handle deleted rows (leftover in initialMap)
    initialMap.forEach((row) => {

      if (!row.PSIC_ID) {
        return;
      }
      payload.push({
        PSIC_ID: row.PSIC_ID || null,
        InHouse_CM_Key: row.inHouse,
        Sourcing_Unit_Region: row.sourcingUnitRegion,
        Sourcing_Unit_country: row.sourcingUnitCountry,
        CM_region: row.cmRegion,
        Cm: row.cms,
        Status: "deleted",
      });
    });
    // return console.log("payload", payload);
    return payload;
  };

  const inhouseCmList = watch("inhouseCmList");


  const onSubmit = async (data: PlanningStrategyFormData, type: any, url: string) => {
    let resField = await fetchRequiredField();
    if (resField?.data && resField?.data.filter((val: any) => val.ModuleName !== 'Planning Strategy').length) {
      const groupedErrors = groupByModule(
        resField?.data.filter((val: any) => val.ModuleName !== 'Planning Strategy')
      );
      setValidationErrors(groupedErrors);
      setIsValidationError(true);
      return;
    }

    try {
      setLoading(true);

      const inhouseCmPayload = buildInhouseCmPayload(
        data.inhouseCmList,
        initialformData.inhouseCmList
      );
      let payload: any = {
        Project_Key: projectKey,
        Volumey1: data.volumeY1,
        Volumey1_Measure: data.volumeY1Unit,
        Volumey3: data.volumeY3,
        Volumey3_Measure: data.volumeY3Unit,
        Cannibalisation: toNullIfEmpty(data.cannibalisation),
        SLA_Complete_Key: toNullIfEmpty(data.slaComplete),
        Avaialble_Capacity: toNullIfEmpty(data.availableCapacity),
        Supplier_Utilisation_Strategy_key: toNullIfEmpty(data.supplierUtilisationStrategy),
        Cpt_Compliance_Key: toNullIfEmpty(data.cptCompliance),
        Comment: toNullIfEmpty(data.comment),
        Risk_Key: toNullIfEmpty(data.risk),
        Inhouse_CM_List: inhouseCmPayload, // <-- NEW ARRAY
      };

      // initial payload for audit
      let initialPayload = {
        Project_Key: projectKey,
        Volumey1: initialformData.volumeY1,
        Volumey1_Measure: initialformData.volumeY1Unit,
        Volumey3: initialformData.volumeY3,
        Volumey3_Measure: initialformData.volumeY3Unit,
        Cannibalisation: toNullIfEmpty(initialformData.cannibalisation),
        SLA_Complete_Key: toNullIfEmpty(initialformData.slaComplete),
        Avaialble_Capacity: toNullIfEmpty(initialformData.availableCapacity),
        Supplier_Utilisation_Strategy_key: toNullIfEmpty(initialformData.supplierUtilisationStrategy),
        Cpt_Compliance_Key: toNullIfEmpty(initialformData.cptCompliance),
        Comment: toNullIfEmpty(initialformData.comment),
        Risk_Key: toNullIfEmpty(initialformData.risk),
        Inhouse_CM_List: JSON.stringify(initialformData.inhouseCmList || []),
      };

      // build audit trail
      const completeAuditTrail = getCompleteAuditTrail(initialPayload, payload);
      payload.audit = completeAuditTrail;

      // Save or update
      if (!isExisting) {
        const res = await createPlanningStrategy(payload as any);
        await pubLishProjectData();
        successToast("Planning Strategy saved successfully!");
      } else {
        const res = await createPlanningStrategy(payload as any);
        await pubLishProjectData();
        if (res) successToast(res.message);
      }

      setProjectDetails({ issaved: true });
      setIsDraftDisabled(true);
      updateAppData({ isFormChanged: false, showUnSavedModal: false, url: appData.url });
      setLoading(false);

      setTimeout(() => {
        navigate(url ? url : `${projectVersion}/project/planning-strategy/${projectId}/${projectKey}`);
      }, 2000);
    } catch (error) {
      console.error("Error submitting form:", error);
      setLoading(false);
    }
  };
  let saveDraft = async (url: string) => {
    try {
      setLoading(true);

      // const draftInhouseCmPayload = watch("inhouseCmList").map((row) => ({
      //   InHouse_CM_Key: toNullIfEmpty(row.inHouse),
      //   Sourcing_Unit_Region: row.inHouse === "39" ? toNullIfEmpty(row.sourcingUnitRegion) : null,
      //   Sourcing_Unit_country: row.inHouse === "39" ? toNullIfEmpty(row.sourcingUnitCountry) : null,
      //   CM_region: row.inHouse === "40" ? toNullIfEmpty(row.cmRegion) : null,
      //   Cm: row.inHouse === "40" ? toNullIfEmpty(row.cms) : null,
      // }));
      const draftInhouseCmPayload = buildInhouseCmPayload(
        watch("inhouseCmList"),
        initialformData.inhouseCmList
      );


      let payload = {
        Project_Key: projectKey,
        Volumey1: watch("volumeY1"),
        Volumey1_Measure: watch("volumeY1Unit"),
        Volumey3: watch("volumeY3"),
        Volumey3_Measure: watch("volumeY3Unit"),
        Cannibalisation: toNullIfEmpty(formData.cannibalisation),
        SLA_Complete_Key: toNullIfEmpty(formData.slaComplete),
        Avaialble_Capacity: toNullIfEmpty(formData.availableCapacity),
        Supplier_Utilisation_Strategy_key: toNullIfEmpty(formData.supplierUtilisationStrategy),
        Cpt_Compliance_Key: toNullIfEmpty(formData.cptCompliance),
        Comment: toNullIfEmpty(formData.comment),
        Risk_Key: toNullIfEmpty(formData.risk),
        Inhouse_CM_List: draftInhouseCmPayload, // <-- NEW ARRAY
      };

      if (!isExisting) {
        await createPlanningStrategy(payload as any);
        successToast("Draft saved successfully!");
      } else {
        const res = await createPlanningStrategy(payload as any);
        if (res) successToast(res.message);
      }

      setLoading(false);
      setTimeout(() => {
        navigate(url ? url : `${projectVersion}/project/engineering-scope/${projectId}/${projectKey}`);
      }, 2000);
    } catch (error) {
      console.error("Error saving draft:", error);
      setLoading(false);
    }
  };

  // let saveDraft = async (url: string) => {
  //   try {
  //     setLoading(true)
  //     let payload = {
  //       Project_Key: projectKey,
  //       Volumey1: watch('volumeY1'),
  //       Volumey1_Measure: watch('volumeY1Unit'),
  //       Volumey3: watch('volumeY3'),
  //       Volumey3_Measure: watch('volumeY3Unit'),
  //       Cannibalisation: toNullIfEmpty(formData.cannibalisation),
  //       InHouse_CM_Key: toNullIfEmpty(formData.inHouse),
  //       Sourcing_Unit_Region: toNullIfEmpty(formData.sourcingUnitRegion),
  //       // Sourcing_Unit_subRegion: toNullIfEmpty(formData.sourcingSubUnitRegion),
  //       Sourcing_Unit_country: toNullIfEmpty(formData.sourcingUnitCountry),
  //       CM_region: toNullIfEmpty(formData.cmRegion),
  //       Cm: toNullIfEmpty(formData.cms),
  //       SLA_Complete_Key: toNullIfEmpty(formData.slaComplete),
  //       Avaialble_Capacity: toNullIfEmpty(formData.availableCapacity),
  //       Supplier_Utilisation_Strategy_key: toNullIfEmpty(
  //         formData.supplierUtilisationStrategy
  //       ),
  //       Cpt_Compliance_Key: toNullIfEmpty(formData.cptCompliance),
  //       Comment: toNullIfEmpty(formData.comment),
  //       Risk_Key: toNullIfEmpty(formData.risk),
  //     };
  //     if (formData.inHouse === "39") {
  //       payload.Cm = null;
  //       payload.CM_region = null;
  //     } else if (formData.inHouse === "40") {
  //       payload.Sourcing_Unit_Region = null;
  //       payload.Sourcing_Unit_country = null;
  //     } 
  //     if (!isExisting) {
  //       {/* @ts-ignore */ }
  //       let res = await createPlanningStrategy(payload);
  //       setLoading(false)
  //       successToast("Planning Strategy saved successfully!");
  //       reset();
  //       setTimeout(() => {
  //         navigate(url ? url : `${projectVersion}/project/engineering-scope/${projectId}/${projectKey}`);
  //       }, 3000);
  //     } else {
  //       {/* @ts-ignore */ }
  //       let res = await createPlanningStrategy(payload, projectKey)
  //       if (projectKey) {
  //         fetchPlanningStrategy(projectKey);
  //       }
  //       setLoading(false)
  //       if (res) {
  //         {/* @ts-ignore */ }
  //         successToast(res.message)
  //         setTimeout(() => {
  //           navigate(`${projectVersion}/project/engineering-scope/${projectId}/${projectKey}`);
  //         }, 2000);
  //       }
  //     }
  //   } catch (error) {
  //     console.error(" Error submitting form:", error);
  //     setLoading(false)
  //   }


  // }


  let handleFormChange = (name: any, value: any) => {
    setFormData({
      ...formData,
      [name]: value,
    });
    hasAutoSaved.current = false;
  };
  useEffect(() => {
    const initialInHouse = formData.inhouseCmList?.[0]?.inHouse;
    if (initialInHouse && inHouseOptions.length > 0) {
      const selectedOption = inHouseOptions.find((item) => item.key === Number(initialInHouse));
      setSelectedInHouse(selectedOption?.key ?? null);
      // console.log('Initial Selected In-House:', selectedOption);
    }
  }, [formData.inhouseCmList, inHouseOptions]);

  let handleFilterChange = async (name: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    hasAutoSaved.current = false;
    if (name === "inHouse") {
      const selectedOption = inHouseOptions.find((item) => item.key === Number(value));
      setSelectedInHouse(selectedOption?.key ?? null);
    }
    if (name === "cmRegion") {

      fetchCM(value); // Fetch CM when CM Region is selected
    }
    if (name === "sourcingUnitRegion") {
      fetchSourceUnitCountry(value); // Fetch country based on region selection
    }
  };

  useEffect(() => {
    // console.log('Updated Selected In-House:', selectedInHouse);
  }, [selectedInHouse]);


  let closeUnSavedModal = () => {
    updateAppData({ isFormChanged: true, showUnSavedModal: false, url: '' });
  }
  let saveUnSavedModal = () => {
    handleFormSubmit(true, appData.url)()
  }
  let draftSavedModal = () => {
    updateAppData({ isFormChanged: false, showUnSavedModal: false, url: appData.url });
    saveDraft(appData.url)
  }
  const discardModal = () => {
    // reset form, redirect, or just close modal
    updateAppData({ isFormChanged: false, showUnSavedModal: false, url: appData.url });
    navigate(appData.url);
  };
  let handleCancel = () => {
    if (!appData.isFormChanged) {
      navigate(`${projectVersion}/projects-grid`)
    } else {
      updateAppData({ isFormChanged: true, showUnSavedModal: true });
    }

  }


  const autoSave = async () => {
    setAutoSaveModal(false);
    try {
      let resField = await fetchRequiredField()
      if (resField?.data && resField?.data.length) {
        await saveDraft('')
        hasAutoSaved.current = true;
      } else {
        await handleFormSubmit(true, '')();
        hasAutoSaved.current = true;
      }
    } catch (error) {
      console.error("Auto-save failed:", error);
    }
  };

  let closeAutoSave = () => {
    setAutoSaveModal(false)
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const uploadFlag = searchParams.get("isUpload") === "true";
    if (uploadFlag) {
      setIsUpload(true);
      searchParams.delete("isUpload");
    }
  }, [location.search, location.pathname]);

  let handlebackUpload = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("isUpload");

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString()
          ? `?${searchParams.toString()}`
          : "", // no query string if empty
      },
      { replace: true }
    );

    setIsUpload(false);
  };

  const handleProjectFileChange = async (file: any) => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',                       // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/vnd.ms-excel',                 // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',       // .xlsx
      'application/vnd.ms-powerpoint',            // .ppt
      'application/vnd.openxmlformats-officedocument.presentationml.presentation' // .pptx
    ];
    if (!allowedTypes.includes(file.type)) {
      errorToast('Only PDF, PPT, XLS or DOC formats are allowed.');
      return;
    }
    setLoading(true)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB in bytes
    if (file.size > MAX_FILE_SIZE) {
      errorToast('File size must be less than 5 MB');
      return;
    }
    try {
      const formdata = new FormData();
      formdata.append("file", file, file.name);
      let data = await uploadProjectFile(projectKey, 5, formdata);
      //  successToast(data.message)
      setLoading(false)
      setRefreshTrigger(prev => prev + 1);

    } catch (error) {
      console.error('Error uploading file:', error);
      errorToast('Failed to upload file. Please try again.');
    }
  };

  let handleClickUpload = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("isUpload", "true");

    navigate(
      {
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      },
      { replace: true }
    );
    setIsUpload(true);
  };


  return (
    <div className="create-project-content">

      <ToastContainer></ToastContainer>
      <Card className="" sx={{ padding: "30px", borderRadius: "20px", pt: 3 }}>
        {/* Buttons - Save, Draft, Cancel */}
        <Box>
          <div className="button-top-form" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Stack
              direction="row"
              spacing={2}
              sx={{ justifyContent: "start", pb: 4 }}
            >
              {
                isUpload ?
                  <Button onClick={() => handlebackUpload()} sx={{ border: '0px', fontSize: '14px', color: '#B5B5B5', p: 0, textTransform: 'capitalize' }}><img src={circleArrowIcon} className="wtsback--img" /></Button>
                  :
                  null
              }

            </Stack>
            {
              !isUpload ?

                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ justifyContent: "end", pb: 4 }}
                >
                  {
                    localStorage.user_info && JSON.parse(localStorage.user_info).Role !== '' ?
                      JSON.parse(localStorage.user_info).Role !== 'ProjectViewer' ?
                        <Tooltip title="Upload Documents" arrow>
                          <Button
                            onClick={() => handleClickUpload()}
                            sx={{ p: 0, minWidth: 'auto', width: '38px', height: '38px', border: '1px solid #000', borderRadius: '50%' }}
                          >
                            <img src={uploadIcon} className="icon-wt-uplad" />
                          </Button>
                        </Tooltip>
                        :
                        <>
                        </>
                      :
                      null
                  }
                  <Button
                    disabled={isDisabled}
                    onClick={handleFormSubmit(true, '')}
                    variant="contained"
                    sx={{
                      borderRadius: "30px",
                      px: 4,
                      py: 1,
                      textTransform: "capitalize",
                      color: 'white !important',
                      backgroundColor: `${isDisabled ? '#d9d9d9 !important' : '#2a3fbf !important'}`
                    }}
                    className={classes.themeButton}
                  >
                    Submit
                  </Button>
                  <Button
                    disabled={isDisabled || projectDetails.issaved}
                    onClick={() => saveDraft('')}
                    variant="contained"
                    sx={{
                      borderRadius: '30px',
                      px: 4,
                      py: 1,
                      textTransform: 'capitalize',
                      color: 'white !important',
                      backgroundColor: '#2a3fbf !important',
                      '&.Mui-disabled': {
                        backgroundColor: '#d9d9d9 !important',
                        color: '#5f6368 !important'
                      }
                    }}
                    className={classes.themeButton}
                  >
                    Draft & Continue
                  </Button>
                  <Button
                    disabled={isDisabled}
                    onClick={handleCancel}
                    variant="contained"
                    sx={{
                      borderRadius: "30px",
                      px: 4,
                      py: 1,
                      textTransform: "capitalize",
                    }}
                    className={classes.cancelButton}
                  >
                    Cancel
                  </Button>
                </Stack>
                :
                <>
                  <div className="file-upload-button">
                    <FileUpload isProjectUpload={true} isDisabled={false} onFileChange={handleProjectFileChange}
                      name="uploadPic" label="Browse" control={control} errors={errors} />
                  </div>
                </>
            }

          </div>
        </Box>

        {
          isUpload ?
            <>
              {/* @ts-ignore */}
              <UploadList refreshTrigger={refreshTrigger} setLoading={setLoading} moduleId={5}></UploadList>
            </>
            :
            <form noValidate>
              <Grid container spacing={3}>
                <Grid size={12}>
                  {
                    !isUpload ?
                      <h5 className="txt-mandatory-title">*Fields are mandatory</h5>
                      :
                      null
                  }

                </Grid>
                <Grid size={4}>
                  <label className="txt-label-input">
                    Volume Y1<span className="required-txt">*</span>
                  </label>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #0000003b",
                      borderRadius: "30px",
                    }}
                  >
                    <Controller
                      name="volumeY1"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          disabled={isDisabled}
                          {...field}
                          sx={{ flex: 1, fieldset: { border: "0px" } }}
                          fullWidth
                          type="number"
                          error={!!errors["volumeY1"]}
                          variant="outlined"
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value); // Pass the correct value
                            clearErrors("volumeY1");
                            handleFormChange("volumeY1", value)
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="volumeY1Unit"
                      control={control}
                      render={({ field }) => (
                        <FormControl sx={{ minWidth: 120 }}>
                          <Select
                            disabled={isDisabled}
                            {...field}
                            displayEmpty
                            value={field.value || ""} // Ensure value is not undefined
                            onChange={(e) => {
                              field.onChange(e.target.value); // Update form 
                              handleFormChange("volumeY1Unit", field.value);
                              clearErrors('volumeY1Unit')
                            }}
                            sx={{
                              fieldset: {
                                border: "0px",
                                borderLeft: "1px solid #0000003b",
                                borderRadius: "0px",
                              },
                              borderRadius: "8px",
                              fontSize: "14px",
                              background: "white",
                              color: !!errors.volumeY1Unit
                                ? "red"
                                : "rgb(196 196 196)",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: !!errors.volumeY1Unit
                                  ? "red"
                                  : "rgb(196 196 196)",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: !!errors.volumeY1Unit ? "red" : "black",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: !!errors.volumeY1Unit ? "red" : "blue",
                              },
                            }}
                          >
                            {volumeY3Unit.map((option) => (
                              <MenuItem
                                key={option.key}
                                value={option.key.toString()}
                              >
                                {option.value}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Box>

                  {errors.volumeY1 && errors.volumeY1Unit ? (
                    <p className="error-message">Volume Y1 and Volume Y1 Unit are required fields.</p>
                  ) : (
                    <>
                      {errors.volumeY1 && (
                        <p className="error-message">{errors.volumeY1.message}</p>
                      )}
                      {errors.volumeY1Unit && (
                        <p className="error-message">{errors.volumeY1Unit.message}</p>
                      )}
                    </>
                  )}

                </Grid>

                <Grid size={4}>
                  <label className="txt-label-input">
                    Volume Y3<span className="required-txt">*</span>
                  </label>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #0000003b",
                      borderRadius: "30px",
                    }}
                  >
                    <Controller
                      name="volumeY3"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          disabled={isDisabled}
                          {...field}
                          sx={{ flex: 1, fieldset: { border: "0px" } }}
                          fullWidth
                          type="number"
                          error={!!errors["volumeY3"]}
                          variant="outlined"
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value); // Pass the correct value
                            clearErrors("volumeY3");
                            handleFormChange("volumeY3", value)

                          }}
                        />
                      )}
                    />

                    <Controller
                      name="volumeY3Unit"
                      control={control}
                      render={({ field }) => (
                        <FormControl sx={{ minWidth: 120 }}>
                          <Select
                            disabled={isDisabled}
                            {...field}
                            displayEmpty
                            value={field.value || ""} // Ensure value is not undefined
                            onChange={(e) => {
                              field.onChange(e.target.value); // Update form state
                              handleFormChange("volumeY3Unit", field.value)
                            }}
                            sx={{
                              fieldset: {
                                border: "0px",
                                borderLeft: "1px solid #0000003b",
                                borderRadius: "0px",
                              },
                              borderRadius: "8px",
                              fontSize: "14px",
                              background: "white",
                              color: !!errors.volumeY3Unit
                                ? "red"
                                : "rgb(196 196 196)",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: !!errors.volumeY3Unit
                                  ? "red"
                                  : "rgb(196 196 196)",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: !!errors.volumeY3Unit ? "red" : "black",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: !!errors.volumeY3Unit ? "red" : "blue",
                              },
                            }}
                          >
                            {volumeY3Unit.map((option) => (
                              <MenuItem
                                key={option.key}
                                value={option.key.toString()}
                              >
                                {option.value}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Box>
                  {errors.volumeY3 && errors.volumeY3Unit ? (
                    <p className="error-message">Volume Y3 and Volume Y3 Unit are required fields.</p>
                  ) : (
                    <>
                      {errors.volumeY3 && (
                        <p className="error-message">{errors.volumeY3.message}</p>
                      )}
                      {errors.volumeY3Unit && (
                        <p className="error-message">{errors.volumeY3Unit.message}</p>
                      )}
                    </>
                  )}
                </Grid>
                <Grid size={4}>
                  <label className="txt-label-input">Cannibalisation </label>
                  <TextInput
                    isDisabled={isDisabled}
                    name="cannibalisation"
                    label=""
                    control={control}
                    errors={errors}
                    onChange={(e) => handleFormChange(e.target.name, e.target.value)}
                    showPercentage={true}
                    percentageWithTwoDecimals={true}
                  />
                </Grid>
              </Grid>
              <Grid size={12} sx={{ mt: 3 }}>
                <div className="cmclassification_parent">
                  <label className="txt-label-input">
                    In-House / CM
                  </label>
                  {fields.map((row, index) => (
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      key={row.id}
                      sx={{ mb: 2 }}
                    >
                      {/* In-House / CM */}
                      <Grid size={3}>
                        <Controller
                          name={`inhouseCmList.${index}.inHouse`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              disabled={isDisabled}
                              {...field}
                              fullWidth
                              sx={{ borderRadius: "20px", background: "white" }}
                              displayEmpty
                            >
                              <MenuItem value="">Select In-House / CM</MenuItem>
                              <MenuItem value="39">In-House</MenuItem>
                              <MenuItem value="40">CM</MenuItem>
                            </Select>
                          )}
                        />
                      </Grid>

                      {/* If In-House selected */}
                      {watch(`inhouseCmList.${index}.inHouse`) === "39" && (
                        <>
                          {/* Sourcing Unit Region */}
                          <Grid size={3}>
                            <Controller
                              name={`inhouseCmList.${index}.sourcingUnitRegion`}
                              control={control}
                              render={({ field }) => (
                                <Select
                                  disabled={isDisabled}
                                  {...field}
                                  fullWidth
                                  sx={{ borderRadius: "20px", background: "white" }}
                                  displayEmpty
                                  onChange={(e) => {
                                    field.onChange(e.target.value);
                                    fetchSourceUnitCountry(Number(e.target.value), row.id); // scoped fetch
                                  }}
                                >
                                  <MenuItem value="">Select Sourcing Unit Region</MenuItem>
                                  {sourcingUnitRegion.map((opt: any) => (
                                    <MenuItem key={opt.key} value={opt.key}>
                                      {opt.value}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                          </Grid>

                          {/* Sourcing Unit Country  changed to factory*/}
                          <Grid size={3}>
                            <Controller
                              name={`inhouseCmList.${index}.sourcingUnitCountry`}
                              control={control}
                              render={({ field }) => {
                                const inhouseCmList = watch("inhouseCmList") || [];

                                // collect selected countries from other rows
                                const selectedCountries = inhouseCmList
                                  .map((row: any, i: number) => (i !== index ? String(row.sourcingUnitCountry) : null))
                                  .filter(Boolean);

                                // filter options to exclude already-selected countries
                                const availableOptions = (sourcingCountryOptions[row.id] || []).filter(
                                  (opt: any) => !selectedCountries.includes(String(opt.key))
                                );

                                return (
                                  <Select
                                    disabled={isDisabled}
                                    {...field}
                                    fullWidth
                                    sx={{ borderRadius: "20px", background: "white" }}
                                    displayEmpty
                                  >
                                    <MenuItem value="">Select Factory</MenuItem>
                                    {availableOptions.map((opt: any) => (
                                      <MenuItem key={opt.key} value={opt.key}>
                                        {opt.value}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                );
                              }}
                            />

                          </Grid>
                        </>
                      )}


                      {/* If CM selected */}
                      {watch(`inhouseCmList.${index}.inHouse`) === "40" && (
                        <>
                          {/* CM Region */}
                          <Grid size={3}>
                            <Controller
                              name={`inhouseCmList.${index}.cmRegion`}
                              control={control}
                              render={({ field }) => (
                                <Select
                                  disabled={isDisabled}
                                  {...field}
                                  fullWidth
                                  sx={{ borderRadius: "20px", background: "white" }}
                                  displayEmpty
                                  onChange={(e) => {
                                    field.onChange(e.target.value);
                                    fetchCM(Number(e.target.value), row.id); // scoped fetch
                                  }}
                                >
                                  <MenuItem value="">Select CM Region</MenuItem>
                                  {cmRegion.map((opt: any) => (
                                    <MenuItem key={opt.key} value={opt.key}>
                                      {opt.value}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                          </Grid>

                          {/* CM */}
                          <Grid size={3}>
                            {/* <Controller
                        name={`inhouseCmList.${index}.cms`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            fullWidth
                            sx={{ borderRadius: "20px", background: "white" }}
                            displayEmpty
                          >
                            <MenuItem value="">Select CM</MenuItem>
                            {(cmsOptions[row.id] || []).map((opt: any) => (
                              <MenuItem key={opt.key} value={opt.key}>
                                {opt.value}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      /> */}
                            <Controller
                              name={`inhouseCmList.${index}.cms`}
                              control={control}
                              render={({ field }) => {
                                const inhouseCmList = watch("inhouseCmList") || [];

                                // collect cms keys from other rows
                                const selectedCms = inhouseCmList
                                  .map((row: any, i: number) => (i !== index ? String(row.cms) : null))
                                  .filter(Boolean);

                                // filter options to exclude already-selected keys
                                const availableOptions = (cmsOptions[row.id] || []).filter(
                                  (opt: any) => !selectedCms.includes(String(opt.key))
                                );

                                return (
                                  <Select
                                    disabled={isDisabled}
                                    {...field}
                                    fullWidth
                                    sx={{ borderRadius: "20px", background: "white" }}
                                    displayEmpty
                                  >
                                    <MenuItem value="">Select CM</MenuItem>
                                    {availableOptions.map((opt: any) => (
                                      <MenuItem key={opt.key} value={opt.key}>
                                        {opt.value}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                );
                              }}
                            />
                          </Grid>
                        </>
                      )}


                      {/* + / - Buttons */}
                      {
                        localStorage.user_info && JSON.parse(localStorage.user_info).Role !== '' ?
                          JSON.parse(localStorage.user_info).Role !== 'ProjectViewer' ?
                            <>
                              <Grid size={3}>
                                <Stack direction="row" spacing={1}>
                                  {/* Always show + */}
                                  <Button
                                    variant="contained"
                                    sx={{
                                      borderRadius: "50%",
                                      minWidth: 32,
                                      width: 32,
                                      height: 31,
                                      paddingTop: 1,
                                      backgroundColor: fields.length >= 6 ? "#d9d9d9" : "#72ded9",
                                      color: "#000",                // black text
                                      fontWeight: "bold",
                                      fontSize: "22px",              // bigger symbol
                                      lineHeight: 1,                // keeps + centered
                                      "&:hover": {
                                        backgroundColor: fields.length >= 6 ? "#d9d9d9" : "#72ded9",
                                      },
                                      // padding: 0,
                                    }}
                                    disabled={fields.length >= 6}
                                    onClick={() =>
                                      append({
                                        inHouse: "",
                                        sourcingUnitRegion: "",
                                        sourcingUnitCountry: "",
                                        cmRegion: "",
                                        cms: "",
                                      })
                                    }
                                  >
                                    +
                                  </Button>

                                  {/* Show - only if row has data OR there is more than one row */}
                                  {(fields[index].inHouse || fields[index].cms || fields.length > 1) && !(fields.length === 1 && !fields[index].inHouse && !fields[index].cms) && (
                                    <Button
                                      variant="contained"
                                      sx={{
                                        borderRadius: "50%",
                                        minWidth: 32,
                                        width: 32,
                                        height: 31,
                                        paddingTop: 0.5,
                                        lineheight: 40,
                                        backgroundColor: "#f2a5a9",   // light red
                                        color: "#000",                // black text
                                        fontWeight: "bold",
                                        fontSize: "24px",             // bigger dash
                                        lineHeight: 1,
                                        "&:hover": {
                                          backgroundColor: "#f2a5a9",
                                        },
                                        // padding: 0,
                                      }}
                                      onClick={() => {
                                        remove(index);
                                        if (fields.length === 1) {
                                          append({
                                            inHouse: "",
                                            sourcingUnitRegion: "",
                                            sourcingUnitCountry: "",
                                            cmRegion: "",
                                            cms: "",
                                          });
                                        }
                                        // optional: clean up options for this row
                                        setCmsOptions((prev) => {
                                          const copy = { ...prev };
                                          delete copy[index];
                                          return copy;
                                        });
                                        setSourcingCountryOptions((prev) => {
                                          const copy = { ...prev };
                                          delete copy[index];
                                          return copy;
                                        });
                                      }}
                                    >
                                      -
                                    </Button>
                                  )}
                                </Stack>
                              </Grid>
                            </>
                            :
                            <></>
                          :
                          <></>
                      }


                    </Grid>
                  ))}
                </div>
              </Grid>

              <div className="cmclassification">
                <Grid container spacing={2} mt={2}>
                  <Grid size={4}>
                    <label className="txt-label-input">
                      SLA Complete
                    </label>
                    <div className="project_drpdnw">
                      <Controller
                        name="slaComplete"
                        control={control}
                        render={({ field }) => (
                          <SelectDropdown
                            isDisabled={isDisabled}
                            filterKey=""
                            label=""
                            value={field.value}
                            onChange={(val) => {
                              field.onChange(val);
                              handleFilterChange("slaComplete", val);
                              clearErrors("slaComplete");
                            }}
                            options={slaComplete}
                            isError={!!errors.slaComplete}
                          />
                        )}
                      />
                    </div>
                  </Grid>

                  <Grid size={4}>
                    <label className="txt-label-input">CU</label>
                    <TextInput
                      isDisabled={isDisabled}
                      name="availableCapacity"
                      label=""
                      control={control}
                      errors={errors}
                      onChange={(e) => handleFormChange(e.target.name, e.target.value)}
                      showPercentage={true}
                      percentageWithTwoDecimals={true}
                    />
                  </Grid>


                </Grid>
                <Grid container spacing={2} mt={2}>
                  <Grid size={4}>
                    <label className="txt-label-input">
                      CPT Compliance
                    </label>
                    <div className="project_drpdnw">
                      <Controller
                        name="cptCompliance"
                        control={control}
                        render={({ field }) => (
                          <SelectDropdown
                            isDisabled={isDisabled}
                            filterKey=""
                            label=""
                            value={field.value}
                            onChange={(val) => {
                              field.onChange(val);
                              handleFilterChange("cptCompliance", val);
                              clearErrors("cptCompliance");
                            }}
                            options={cptCompliance}
                            isError={!!errors.cptCompliance}
                          />
                        )}
                      />
                    </div>
                  </Grid>
                  <Grid size={4}>
                    <label className="txt-label-input">Proactive LTCP</label>
                    <div className="project_drpdnw">
                      <Controller
                        name="supplierUtilisationStrategy"
                        control={control}
                        render={({ field }) => (
                          <SelectDropdown
                            isDisabled={isDisabled}
                            filterKey=""
                            label=""
                            value={field.value}
                            onChange={(val) => {
                              field.onChange(val);
                              handleFilterChange("supplierUtilisationStrategy", val);
                              clearErrors("supplierUtilisationStrategy");
                            }}
                            options={supplierUtilisationStrategy}
                            isError={!!errors.supplierUtilisationStrategy}
                          />
                        )}
                      />
                    </div>
                  </Grid>
                </Grid>
              </div>
              {/* </Grid> */}
              <Grid container spacing={2} mt={2}>
                <Grid size={8}>
                  <label className="txt-label-input">Comment</label>
                  <TextInput
                    isDisabled={isDisabled}
                    className="comment-input" isComment={true}
                    onChange={(e) => handleFormChange(e.target.name, e.target.value)}
                    name="comment"
                    label=""
                    control={control}
                    errors={errors}
                    multiline
                  />
                </Grid>

                <Grid size={4}>
                  <label className="txt-label-input">Planning Strategy Risk</label>
                  <div className="project_drpdnw">
                    <Controller
                      name="risk"
                      control={control}
                      render={({ field }) => (
                        <SelectDropdown
                          isDisabled={isDisabled}
                          filterKey=""
                          label=""
                          value={field.value}
                          onChange={(val) => {
                            field.onChange(val);
                            handleFilterChange("risk", val);
                            clearErrors("risk");
                          }}
                          options={risks}
                          isError={errors.risk ? true : false}
                        />
                      )}
                    />
                  </div>
                  {errors.risk && (
                    <p className="error-message">{errors.risk.message}</p>
                  )}
                </Grid>
              </Grid>
            </form>
        }


        {/* @ts-ignore */}
        <UnSavedModal close={closeUnSavedModal} saveUnSavedModal={saveUnSavedModal} draftSavedModal={draftSavedModal} discardModal={discardModal} isDraftDisabled={isDraftDisabled} />
        <ValidationModal validationErrors={validationErrors} open={isValidationError} closeValidationModal={closeValidationModal} />
        <AutoSaveModal open={autoSaveModal} closeAutoSave={closeAutoSave} autoSave={autoSave} ></AutoSaveModal>

      </Card>
    </div>

  );
};

export default PlanningStrategyForm;
